import React, { useEffect, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { bindActionCreators } from 'redux'
import { ToastContainer } from 'react-toastify'

import { usePrevProp } from 'utils/hooks'
import screenType from 'component/menuMission/screenType'
import Col from 'component/utils/Col'
import ContentHeader from 'component/utils/ContentHeader'
import Grid from 'component/utils/Grid'
import Notification from 'component/utils/Notification'
import Options from 'component/justificationPa/Options'
import Pagination from 'component/pagination/Pagination'
import SideFilterForm from 'component/justificationPa/SideFilterForm'
import Table from 'component/justificationPa/Table'

import 'styles/overwrittenAndNew/newPageStyle.css'

// Modals
import EditModal from 'component/justificationPa/EditModal'
import SuccessModal from 'component/Modal/SuccessModal'

import * as justificationsPaActions from 'action/justificationsPa'

/**
 * Control which API should be called
 */
const SHOULD_SAVE = 1
const SHOULD_UPDATE = 2

const DEFAULT_FILTER_INITIAL_VALUES = {
  inInactive: 2,
}

const DEFAULT_EDIT_INITIAL_VALUES = {
  params: [
    {
      dsJustification: '',
      inInactive: 0,
    },
  ],
}

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const JustificationsPa = ({
  auth: {
    user: { dsToken },
  },
  count,
  current,
  fetchJustificationsPaList,
  pages,
  records = [],
  removeJustificationPa,
  translate,
  updateJustificationPa,
}) => {
  const [openEditModal, setOpenEditModal] = useState(false)
  const [openSuccessModal, setOpenSuccessModal] = useState(null)

  const [editInitialValues, setEditInitialValues] = useState(
    DEFAULT_EDIT_INITIAL_VALUES
  )
  const [shouldSaveOrUpdate, setShouldSaveOrUpdate] = useState(SHOULD_SAVE)

  const [filters, setFilters] = useState(DEFAULT_FILTER_INITIAL_VALUES)
  const prevFilters = usePrevProp(filters)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const prevPagination = usePrevProp(pagination)
  const [sideFilterVisible, setSideFilterVisible] = useState(false)

  /**
   * When filters change
   */
  useEffect(() => {
    if (filters !== prevFilters) {
      if (pagination.page > 1) {
        setPagination({ ...pagination, page: 1 })
      } else {
        handleFetch()
      }
    }
  }, [filters])

  /**
   * When pagination changes
   */
  useEffect(() => {
    if (pagination !== prevPagination) {
      handleFetch()
    }
  }, [pagination])

  /**
   * Handle data fetch
   */
  const handleFetch = () => {
    const param = {
      ...filters,
      ...pagination,
    }

    fetchJustificationsPaList(dsToken, param)
  }

  /**
   * Set new filters
   */
  const setValues = (values) => {
    setFilters({ ...filters, ...values, o: Math.random })
  }

  /**
   * Render pagination
   */
  const renderPagination = () => (
    <Col className='align-center margin-bottom margin-top-triple new-style-pagination'>
      {!!records.length && pages > 1 && (
        <Pagination
          count={count}
          current={current}
          getPage={(page) => setPagination({ ...pagination, page })}
          pages={pages}
        />
      )}
    </Col>
  )

  /**
   * Render modal to create record
   */
  const renderEditModal = () => {
    if (!openEditModal) return

    return (
      <EditModal
        closeModal={() => setOpenEditModal(false)}
        initialValues={editInitialValues}
        submit={handleSave}
        token={dsToken}
        translate={translate}
      />
    )
  }

  /**
   * Render success modal
   */
  const renderSuccessModal = () => {
    if (!openSuccessModal) return

    return (
      <SuccessModal
        handleClose={() => setOpenSuccessModal(null)}
        msg={translate(
          `config.pricebannerConfig.saveSuccess.${openSuccessModal}`
        )}
        size='md'
        t={translate}
      />
    )
  }

  /**
   * Render side filter
   */
  const renderSideFilterForm = () => {
    if (!sideFilterVisible) return

    const handleSubmitSideFilterForm = (values) => {
      setSideFilterVisible(false)

      setValues(values)
    }

    return (
      <SideFilterForm
        initialValues={filters}
        setSideFilterVisible={toggleSideFilter}
        submit={handleSubmitSideFilterForm}
        translate={translate}
      />
    )
  }

  /**
   * Get values and manage modals while saving
   */
  const handleSave = async (values) => {
    setOpenEditModal(false)

    if (shouldSaveOrUpdate === SHOULD_SAVE) {
      const callback = () => {
        setOpenSuccessModal(screenType.CREATE_PERSONAL_PROFILE_TYPE)

        setEditInitialValues({
          params: [{ dsJustification: '', inInactive: 0 }],
        })
      }

      await updateJustificationPa(dsToken, values, callback)
    } else {
      await updateJustificationPa(dsToken, values, () =>
        setOpenSuccessModal(screenType.EDIT_PERSONAL_PROFILE_TYPE)
      )
    }

    handleFetch()
  }

  /**
   * Toggle side filter
   */
  const toggleSideFilter = () => {
    setSideFilterVisible(!sideFilterVisible)
  }

  /**
   * Render upper section with filter and buttons
   */
  const renderUpperSection = () => {
    /**
     * Handle modal to edit record
     */
    const handleOpenEditModal = () => {
      setEditInitialValues({
        params: [{ dsJustification: '', inInactive: 0 }],
        type: screenType.CREATE_PERSONAL_PROFILE_TYPE,
      })

      setOpenEditModal(true)

      setShouldSaveOrUpdate(SHOULD_SAVE)
    }

    return (
      <div className='new-style'>
        <Grid fluid className='new-upper-container'>
          <Col xs={12} md={6} className='float-right'>
            <Options
              setOpenEditModal={handleOpenEditModal}
              setSideFilterVisible={toggleSideFilter}
              translate={translate}
            />
          </Col>
        </Grid>
      </div>
    )
  }

  /**
   * Render data table
   */
  const renderTable = () => {
    const callback = () => {
      setOpenSuccessModal(screenType.REMOVE_TYPE)

      handleFetch()
    }

    const handleDelete = (value) =>
      removeJustificationPa(dsToken, value, callback)

    const handleOpenEditModal = (record) => {
      setShouldSaveOrUpdate(SHOULD_UPDATE)

      const updateData = {
        params: [record],
        type: screenType.EDIT_PERSONAL_PROFILE_TYPE,
      }

      setEditInitialValues(updateData)

      setOpenEditModal(true)
    }

    return records.length ? (
      <Table
        handleDelete={handleDelete}
        openEditModal={handleOpenEditModal}
        records={records}
        toggleInInactive={(values) => updateJustificationPa(dsToken, values)}
        translate={translate}
      />
    ) : (
      <Col
        xs={12}
        className='margin-top margin-right'
        style={{ marginLeft: `${sideFilterVisible ? 0.5 : 1}rem` }}
      >
        <Notification
          message={translate('no.data.found')}
          className='notification-warning'
        />
      </Col>
    )
  }

  return (
    <Fragment>
      <div className='new-style'>
        <ContentHeader>
          {translate('justificationsPa.title.screen')}
        </ContentHeader>
        <div className='padding-bottom new-style-filter'>
          {renderUpperSection()}
        </div>
        <div className='new-style-table'>
          <Grid fluid className='no-padding'>
            <div className='outer-table new-outer-table'>
              <div>{renderTable()}</div>

              {renderPagination()}
            </div>
          </Grid>
        </div>

        {renderEditModal()}
        {renderSuccessModal()}

        <ToastContainer newestOnTop />
      </div>

      {renderSideFilterForm()}
    </Fragment>
  )
}

/* istanbul ignore next */
const mapStateToProps = ({ justificationPaReducer, paginateReducer }) => ({
  error: justificationPaReducer.edit.error,
  count: paginateReducer.count,
  current: paginateReducer.current,
  pages: paginateReducer.pages,
  records: justificationPaReducer.list.records,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(justificationsPaActions, dispatch)

/*
 * Exports component connected to redux as default
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    JustificationsPa
  )
)

JustificationsPa.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  removeJustificationPa: PropTypes.func.isRequired,
  fetchJustificationsPaList: PropTypes.func.isRequired,
  pages: PropTypes.number.isRequired,
  records: PropTypes.array.isRequired,
  translate: PropTypes.func.isRequired,
  updateJustificationPa: PropTypes.func.isRequired,
}

export { JustificationsPa }
